// Main App Component with Routing
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import CookieConsent from './components/CookieConsent';
import FirstTripTutorial from './components/FirstTripTutorial';
import './i18n';
import './App.css';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
// Lazy load other pages for better performance
const Trips = React.lazy(() => import('./pages/Trips'));
const Expenses = React.lazy(() => import('./pages/Expenses'));
const Invoices = React.lazy(() => import('./pages/Invoices'));
const Fleet = React.lazy(() => import('./pages/Fleet'));
const Accounting = React.lazy(() => import('./pages/Accounting'));
const AccountantSpace = React.lazy(() => import('./pages/AccountantSpace'));
const Settings = React.lazy(() => import('./pages/Settings'));
// New pages
const TripConfidenceDashboard = React.lazy(() => import('./pages/TripConfidenceDashboard'));
const AccountantPortal = React.lazy(() => import('./pages/AccountantPortal'));
const AuditHistory = React.lazy(() => import('./pages/AuditHistory'));
const VehicleMaintenance = React.lazy(() => import('./pages/VehicleMaintenance'));
const GPSNavigation = React.lazy(() => import('./pages/GPSNavigation'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <React.Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    }>
      <Routes>
        {/* Landing Page */}
        <Route path="/" element={<Landing />} />

        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trips"
          element={
            <ProtectedRoute>
              <Trips />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses"
          element={
            <ProtectedRoute>
              <Expenses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <Invoices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fleet"
          element={
            <ProtectedRoute>
              <Fleet />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounting"
          element={
            <ProtectedRoute>
              <Accounting />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accountant-space"
          element={
            <ProtectedRoute>
              <AccountantSpace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/confidence"
          element={
            <ProtectedRoute>
              <TripConfidenceDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accountant-portal"
          element={
            <ProtectedRoute>
              <AccountantPortal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <ProtectedRoute>
              <AuditHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance"
          element={
            <ProtectedRoute>
              <VehicleMaintenance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gps"
          element={
            <ProtectedRoute>
              <GPSNavigation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        {/* Redirect unknown routes to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}

// Tutorial wrapper for authenticated users
const TutorialWrapper = () => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) return null;
  
  return <FirstTripTutorial />;
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <TutorialWrapper />
          <Toaster position="top-right" />
          <CookieConsent />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
